import React, { useEffect, useState } from "react";
import { RotateCcw, PenLine } from "lucide-react";

const LINES = [40, 60, 80, 100, 120];
const STEP_S = 1.1;
const CLEFS = {
  treble: {
    title: "How to draw the Treble Clef",
    x: 0,
    steps: [
      { say: "Start on the G line (the 2nd line)", dot: [70, 100, 4] },
      { say: "Curl up to the next line", d: "M70,100 C70,78 88,70 93,90" },
      { say: "Curl down to the bottom line", d: "M93,90 C96,112 80,122 68,120" },
      { say: "Continue around", d: "M68,120 C50,118 44,98 56,84" },
      { say: "Loop at the top", d: "M56,84 C68,70 86,56 84,30 C82,14 70,12 66,26" },
      { say: "Then straight down, finishing the tail with a small blob", d: "M66,26 C64,40 74,110 78,140 C80,152 66,158 60,150", blob: [62, 147, 7] },
    ],
  },
  bass: {
    title: "How to draw the Bass Clef",
    x: 90,
    steps: [
      { say: "Start with the dot on the 4th line", dot: [150, 60, 7] },
      { say: "Curl up to touch the top line", d: "M150,60 C148,44 168,36 182,44" },
      { say: "Curl round and down through the stave", d: "M182,44 C198,54 192,84 168,100 C160,106 150,112 140,116" },
      { say: "Add the two dots, either side of the 4th line", dots: [[204, 50], [204, 70]] },
    ],
  },
};

const Seg = ({ s, i }) => {
  const style = { animationDelay: `${i * STEP_S}s` };
  if (s.d) return <>
    <path d={s.d} pathLength="1" className="clef-draw-stroke" style={style} fill="none" stroke="hsl(var(--primary))" strokeWidth="4.5" strokeLinecap="round" />
    {s.blob && <circle cx={s.blob[0]} cy={s.blob[1]} r={s.blob[2]} className="clef-draw-dot" style={{ animationDelay: `${(i + 1) * STEP_S}s` }} fill="hsl(var(--primary))" data-testid="clef-draw-tail-blob" />}
  </>;
  const dots = s.dot ? [s.dot] : s.dots.map(([x, y]) => [x, y, 4]);
  return dots.map(([x, y, r]) => <circle key={`${x}-${y}`} cx={x} cy={y} r={r} className="clef-draw-dot" style={style} fill="hsl(var(--primary))" />);
};

export const ClefDrawing = ({ clef = "treble", testId = "clef-drawing" }) => {
  const c = CLEFS[clef];
  const [run, setRun] = useState(0);
  const [step, setStep] = useState(0);
  useEffect(() => {
    setStep(0);
    const ids = c.steps.map((_, i) => setTimeout(() => setStep(i), i * STEP_S * 1000));
    return () => ids.forEach(clearTimeout);
  }, [run, c]);
  return (
    <div className="mt-5 rounded-2xl border border-primary/30 bg-card p-4 sm:p-6" data-testid={testId}>
      <style>{`.clef-draw-stroke{stroke-dasharray:1 2;stroke-dashoffset:1;animation:clefDraw ${STEP_S}s ease-in-out forwards}.clef-draw-dot{opacity:0;animation:clefDot .4s ease-out forwards}@keyframes clefDraw{to{stroke-dashoffset:0}}@keyframes clefDot{to{opacity:1}}@media (prefers-reduced-motion:reduce){.clef-draw-stroke,.clef-draw-dot{animation-duration:.01s}}`}</style>
      <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-primary"><PenLine size={14} /> {c.title}</p>
      <div className="mt-3 grid items-center gap-4 sm:grid-cols-[minmax(0,320px)_1fr]">
        <svg key={run} viewBox={`${20 + c.x} 0 170 170`} className="w-full max-w-xs" role="img" aria-label={`${c.title}: ${c.steps.map((s) => s.say).join(", ")}`} data-testid={`${testId}-svg`}>
          {LINES.map((y) => <line key={y} x1={20 + c.x} x2={190 + c.x} y1={y} y2={y} stroke="var(--staff-line)" strokeWidth="1.4" />)}
          {c.steps.map((s, i) => <Seg key={i} s={s} i={i} />)}
        </svg>
        <ol className="space-y-1.5 text-sm" data-testid={`${testId}-steps`}>
          {c.steps.map((s, i) => (
            <li key={s.say} data-testid={`${testId}-step-${i + 1}`} className={`rounded-lg px-3 py-1.5 transition-colors ${i === step ? "bg-primary/15 font-semibold text-foreground" : i < step ? "text-foreground" : "text-muted-foreground"}`}>
              {i + 1}. {s.say}
            </li>
          ))}
        </ol>
      </div>
      <button onClick={() => setRun((r) => r + 1)} data-testid={`${testId}-replay`} className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-xs font-semibold hover:border-primary"><RotateCcw size={12} /> Draw it again</button>
      <p className="mt-2 text-xs text-muted-foreground">Now draw it yourself on your manuscript paper, following the same steps.</p>
    </div>
  );
};
